<script lang="ts">
  import { cn } from '$lib/utils';
  import { t } from '$lib/i18n';
  import { page } from '$app/stores';
  import { derived } from 'svelte/store';
  import { createQuery } from '@tanstack/svelte-query';
  import type { GetProductQuery, ProductCardFragment } from '$gql/storefront/operations';
  import type { ProductCardTagStoryblok } from '$types/bloks';
  import {
    categoryCampaigns,
    getCampaignColors,
    tieredCampaign,
    type CampaignColors
  } from '$lib/stores/campaigns';
  import { getBottomlineData, type Bottomline } from '$lib/yotpo';
  import ProductBadge from './product-badge.svelte';

  export let product: ProductCardFragment | NonNullable<GetProductQuery['product']>;
  export let stylesClass: string | undefined = undefined;

  const getAllBadges = derived(
    [page],
    ([$page]) =>
      (
        product: ProductCardFragment | NonNullable<GetProductQuery['product']>
      ): { label: string; colors?: CampaignColors }[] | undefined => {
        const labels = $page.data.configuration.content.product_card_labels.filter(
          ({ shopify_id }) => product.tags.includes(shopify_id)
        );

        return labels
          ? labels.map((label: ProductCardTagStoryblok) => ({
              label: label.text,
              colors: label.color ? getCampaignColors(label.color) : undefined
            }))
          : undefined;
      }
  );

  $: tagBadges = $getAllBadges(product);

  $: outOfStockBadge = !product.availableForSale
    ? { label: $t('pdp.no-stock'), colors: undefined }
    : undefined;

  $: yotpo = createQuery<Bottomline>({
    staleTime: Infinity,
    enabled: !tagBadges || tagBadges.length === 0,
    queryKey: ['reviewBottomline', product.id],
    queryFn: async () => {
      return await getBottomlineData(product.id);
    }
  });

  $: review = (() => {
    if (
      $yotpo.data?.bottomline.average_score &&
      $yotpo.data?.bottomline.average_score >=
        +$page.data.configuration.content.product_card_threshold_score &&
      $yotpo.data?.bottomline.total_reviews &&
      $yotpo.data?.bottomline.total_reviews >=
        +$page.data.configuration.content.product_card_threshold_review_number
    )
      return {
        label: $yotpo.data?.bottomline.average_score.toFixed(1),
        icon: 'star-filled' as const,
        color: undefined
      };
  })();

  $: hasTieredCampaign = !!$tieredCampaign && !$tieredCampaign.isProductExcluded(product);

  $: hasCategoryCampaign =
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => !campaign.isProductExcluded(product)) &&
    !hasTieredCampaign;

  $: categoryCampaign = $categoryCampaigns?.find(
    (campaign) => !campaign.isProductExcluded(product)
  );

  $: categoryCampaignBadge = (() => {
    if (!hasCategoryCampaign || !categoryCampaign) return;
    const colors = categoryCampaign.campaign_color
      ? getCampaignColors(categoryCampaign.campaign_color)
      : undefined;
    return {
      label: $t('category-campaign.card-badge', { discount: categoryCampaign.discount }),
      colors
    };
  })();
</script>

{#if ((tagBadges && tagBadges.length > 0) || review || hasCategoryCampaign || outOfStockBadge) && !$yotpo.isFetching}
  <div class={cn('flex gap-1 flex-wrap', stylesClass)}>
    {#if outOfStockBadge}
      <ProductBadge {...outOfStockBadge} />
    {:else}
      {#if categoryCampaignBadge}
        <ProductBadge {...categoryCampaignBadge} />
      {/if}
      {#if tagBadges}
        {#each tagBadges as tagBadge}
          <ProductBadge {...tagBadge} />
        {/each}
      {/if}
      {#if review && !((hasCategoryCampaign && categoryCampaign) || (tagBadges && tagBadges?.length > 0))}
        <ProductBadge {...review} />
      {/if}
    {/if}
  </div>
{/if}
